// FILE: src/components/Contacts.js (hide Import/Template buttons on mobile only)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  collection,
  query,
  where,
  onSnapshot,
    deleteDoc,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import ContactForm from "./ContactForm";
import Modal from "./ui/Modal";
import VideoLightbox from "./ui/VideoLightbox";
import "../styles/layout.css";
// Owner print/download (unchanged)
import { printEntry, downloadEntry } from "../utils/printDownload";
import { FiEye, FiUpload, FiDownload } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import { useAccess } from "../access/AccessContext";
import { AddContactModal, CONTACT_CATEGORIES } from "../utils/contacts-ui";

// Read-only View modal helpers (global)
import { printNode } from "../lib/print";
import { downloadNodeAsPdf } from "../lib/pdfDownload";
import { forwardModal } from "../lib/forward";

const CATEGORIES = CONTACT_CATEGORIES;
const REQUIRED_IMPORT_FIELDS = ["firstName", "lastName", "email", "phone", "category"];
const CSV_TEMPLATE_CONTENT = `${REQUIRED_IMPORT_FIELDS.join(",")}\nJane,Doe,jane@example.com,555-123-4567,Family\n`;

function parseCsv(text = "") {
  const rows = [];
  let current = "";
  let row = [];
  let inQuotes = false;
  const input = text.replace(/\r\n/g, "\n");

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    if (char === '"') {
      if (inQuotes && input[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      row.push(current);
      current = "";
    } else if (char === "\n" && !inQuotes) {
      row.push(current);
      rows.push(row);
      row = [];
      current = "";
    } else {
      current += char;
    }
  }

  if (current.length || row.length) {
    row.push(current);
    rows.push(row);
  }

  return rows.filter((r) => r.some((cell) => String(cell || "").trim() !== ""));
}

function splitName(full = "") {
  const parts = String(full || "").trim().split(/\s+/);
  if (parts.length <= 1) return { firstName: parts[0] || "", lastName: "" };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts.slice(-1).join(" ") };
}

export default function Contacts({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  const [contacts, setContacts] = useState([]);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [activeCat, setActiveCat] = useState(CATEGORIES[0]);

  // View summary modal (for read-only)
  const [viewContact, setViewContact] = useState(null);
  const viewRef = useRef(null);

  // Global Add modal (unchanged)
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importErr, setImportErr] = useState("");
  const [importSummary, setImportSummary] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Edit form state
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [ef, setEf] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    category: CATEGORIES[0] || "Friends",
  });

  // Modern category dropdown open flag
  const [catOpen, setCatOpen] = useState(false);

  const catRefs = useRef(Object.fromEntries(CATEGORIES.map((c) => [c, React.createRef()])));
  const filterBarRef = useRef(null);

  useEffect(() => {
    if (!editingContact) return;
    const t = setTimeout(() => {
      try {
        if (typeof document !== "undefined") {
          const active = document.activeElement;
          if (active && typeof active.blur === "function") {
            active.blur();
          }
        }
      } catch {}
    }, 0);
    return () => clearTimeout(t);
  }, [editingContact]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "contacts"),
      where("uid", "==", user.uid)
    );
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const relA = String(a.relationship || "").toLowerCase();
        const relB = String(b.relationship || "").toLowerCase();
        if (relA !== relB) return relA.localeCompare(relB);
        const nameA = String(a.name || "").toLowerCase();
        const nameB = String(b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setContacts(rows);
    });
  }, [user?.uid]);

  // Remove read-only blocker overlay if present (managed account shell)
  useEffect(() => {
    if (!readOnly || typeof document === "undefined") return;
    const selector = '[title="Read-only while managing another account"]';
    const removeMask = () => {
      document.querySelectorAll(selector).forEach((el) => {
        try { el.remove(); } catch {}
      });
    };
    removeMask();
    const mo = new MutationObserver(() => removeMask());
    try { mo.observe(document.body, { childList: true, subtree: true }); } catch {}
    return () => { try { mo.disconnect(); } catch {} };
  }, [readOnly]);

  useEffect(() => {
    if (showImport) {
      setImportErr("");
      setImportSummary("");
      setImportBusy(false);
      setDragActive(false);
    }
  }, [showImport]);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(CATEGORIES.map((c) => [c, []]));
    contacts.forEach((c) => {
      const rel = CATEGORIES.includes(c.relationship) ? c.relationship : "Other";
      map[rel].push(c);
    });
    return map;
  }, [contacts]);

  function openForm(contact = {}) {
    if (!contact?.id && free && contacts.length >= 1) { setShowUpgrade(true); return; }
    if (readOnly) return;
    setEditingContact(contact?.id ? contact : {});
    const { firstName = "", lastName = "" } =
      contact?.firstName || contact?.lastName
        ? { firstName: contact.firstName || "", lastName: contact.lastName || "" }
        : splitName(contact?.name || "");
    setEf({
      firstName,
      lastName,
      email: contact?.email || "",
      phone: contact?.phone || "",
      category: CATEGORIES.includes(contact?.relationship)
        ? contact.relationship
        : (CATEGORIES[0] || "Friends"),
    });
    setEditErr("");
    setEditBusy(false);
    setCatOpen(false);
  }
  function closeForm() {
    setEditingContact(null);
    setEditErr("");
    setEditBusy(false);
    setCatOpen(false);
  }

  async function handleDelete(contact) {
    if (readOnly) return;
    if (!contact?.id) return;
    if (!window.confirm(`Delete contact “${contact.name || "(no name)"}”?`)) return;
    try {
      await deleteDoc(doc(db, "contacts", contact.id));
      if (editingContact?.id === contact.id) closeForm();
    } catch (err) {
      alert("Delete failed: " + (err?.message || String(err)));
    }
  }

  // Owner tools
  function handlePrintOwner() {
    if (!editingContact?.id) return;
    printEntry(editingContact, {
      title: editingContact?.name ? `Contact — ${editingContact.name}` : "Contact",
      imageKey: "photoUrl",
    });
  }
  function handleDownloadOwner() {
    if (!editingContact?.id) return;
    const filename = `contact-${(editingContact?.name || "entry").replace(/\s+/g, "-").toLowerCase()}.html`;
    downloadEntry(editingContact, {
      filename,
      title: editingContact?.name ? `Contact — ${editingContact.name}` : "Contact",
      imageKey: "photoUrl",
    });
  }

  function downloadTemplateCsv() {
    try {
      const blob = new Blob([CSV_TEMPLATE_CONTENT], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "contacts-template.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Download failed: " + (e?.message || String(e)));
    }
  }

  async function handleImportFile(file) {
    if (!file || !user?.uid) {
      if (!user?.uid) {
        setImportErr("Please sign in to import contacts.");
      }
      return;
    }

    setImportBusy(true);
    setImportErr("");
    setImportSummary("");

    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) throw new Error("No data rows were found in that CSV file.");

      const headerRow = rows[0].map((cell, idx) =>
        (idx === 0 ? String(cell || "").replace(/^\uFEFF/, "") : cell || "").trim()
      );
      const headerMap = new Map(headerRow.map((name, idx) => [name.toLowerCase(), idx]));
      const missingColumns = REQUIRED_IMPORT_FIELDS.filter(
        (col) => !headerMap.has(col.toLowerCase())
      );
      if (missingColumns.length) throw new Error(`Missing columns: ${missingColumns.join(", ")}.`);

      const payloads = [];
      for (let i = 1; i < rows.length; i += 1) {
        const row = rows[i];
        if (!row || !row.some((cell) => String(cell || "").trim() !== "")) continue;

        const record = {};
        REQUIRED_IMPORT_FIELDS.forEach((field) => {
          const idx = headerMap.get(field.toLowerCase());
          record[field] = String(row[idx] ?? "").trim();
        });

        const missingField = REQUIRED_IMPORT_FIELDS.find((field) => !record[field]);
        if (missingField) throw new Error(`Row ${i + 1}: ${missingField} is required.`);

        const category = CATEGORIES.find(
          (cat) => cat.toLowerCase() === record.category.toLowerCase()
        );
        if (!category) {
          throw new Error(
            `Row ${i + 1}: category must be one of ${CATEGORIES.join(", ")}.`
          );
        }

        payloads.push({
          uid: user.uid,
          name: `${record.firstName} ${record.lastName}`.trim(),
          firstName: record.firstName,
          lastName: record.lastName,
          email: record.email,
          phone: record.phone,
          relationship: category,
          created: serverTimestamp(),
        });
      }

      if (!payloads.length) throw new Error("We didn't find any complete rows to import.");

      await Promise.all(payloads.map((payload) => addDoc(collection(db, "contacts"), payload)));
      setImportSummary(`${payloads.length} contacts imported successfully.`);
    } catch (err) {
      setImportErr(err?.message || String(err));
    } finally {
      setImportBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // Read-only View actions
  function viewPrint() {
    if (!viewRef.current) return;
    printNode(viewRef.current);
  }
  async function viewDownload() {
    if (!viewRef.current || !viewContact) return;
    const filename = `contact-${(viewContact?.name || "entry").replace(/\s+/g, "-").toLowerCase()}.pdf`;
    await downloadNodeAsPdf(viewRef.current, filename);
  }
  async function viewForward() {
    if (!viewRef.current || !viewContact) return;
    const filename = `contact-${(viewContact?.name || "entry").replace(/\s+/g, "-").toLowerCase()}.pdf`;
    const { pdfBase64 } = await downloadNodeAsPdf(viewRef.current, filename, { returnBase64: true });
    const to = window.prompt("Send to (email address):");
    if (!to) return;
    const subject = viewContact?.name ? `Contact — ${viewContact.name}` : "Contact details";
    const html = `<p>Please find attached a PDF of the contact details.</p>`;
    try {
      await forwardModal({
        to,
        subject,
        html,
        attachments: [{ filename, contentType: "application/pdf", base64: pdfBase64 }],
      });
      alert("Email sent.");
    } catch (e) {
      alert(e?.message || "Forward failed");
    }
  }

  useEffect(() => {
    const sections = CATEGORIES.map((c) => catRefs.current[c]?.current).filter(Boolean);
    if (!sections.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].getAttribute("data-cat");
          if (id) setActiveCat(id);
        }
      },
      { rootMargin: "-120px 0px -70% 0px", threshold: [0, 0.25, 0.6] }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [grouped]);

  function jumpTo(cat) {
    setActiveCat(cat);
    const el = catRefs.current[cat]?.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const ActionsDesktop = () => (
    <div className="dp-help-row">
      <button
        data-allow-readonly="1"
        type="button"
        className="btn dp-help read"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => setShowDrawer(true)}
        aria-haspopup="dialog"
      >
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button
        data-allow-readonly="1"
        type="button"
        className="btn dp-help see"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => setShowVideo(true)}
        aria-haspopup="dialog"
      >
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );
  const ActionsMobile = ActionsDesktop;

  return (
    <div className="container">
      <div className="header-row">
        <h2>Contacts</h2>
        <div className="header-actions desktop-only"><ActionsDesktop /></div>
        {!readOnly && <button className="btn-add" onClick={() => { if (free && contacts.length >= 1) { setShowUpgrade(true); } else { setShowAdd(true); } }}>+</button>}
      </div>

      <div className="hero-wrap">
        <img src="/lastwish-account-contacts.jpg" alt="Contacts" className="hero-img" />
      </div>

      {/* These two buttons (Import / Download template) are hidden on mobile via CSS below */}
      <div className="hero-actions">
        <button
          type="button"
          className="btn btn-main hero-btn import-btn"
          onClick={() => setShowImport(true)}
          disabled={readOnly}
        >
          <FiUpload aria-hidden="true" /> Import contacts
        </button>
        <button
          type="button"
          className="btn hero-btn template-btn"
          onClick={downloadTemplateCsv}
          data-allow-readonly="1"
        >
          <FiDownload aria-hidden="true" /> Download CSV template
        </button>
      </div>

      <div className="mobile-only" style={{ margin: "10px 0" }}>
        <ActionsMobile />
      </div>

      {/* Sticky category filter */}
      <div ref={filterBarRef} className="cat-filter" data-allow-readonly="1">
        <div className="cat-pills">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className={`pill ${activeCat === c ? "active" : ""}`}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => jumpTo(c)}
              data-allow-readonly="1"
            >
              {c}
            </button>
          ))}
        </div>
        <div className="cat-select select-field">
          <select
            value={activeCat}
            onChange={(e) => jumpTo(e.target.value)}
            data-allow-readonly="1"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <span className="select-field__caret" aria-hidden="true">▾</span>
        </div>
      </div>

      {/* Sections */}
      <div className="contacts-groups">
        {CATEGORIES.map((section) =>
          grouped[section]?.length ? (
            <section
              className="contacts-section"
              key={section}
              ref={catRefs.current[section]}
              data-cat={section}
            >
              <div className="section-title small">{section}</div>
              <div className="contacts-list">
                {grouped[section].map((contact) => (
                  <div
                    key={contact.id}
                    className="contact-row"
                    data-allow-readonly="1"
                    onClick={readOnly ? () => setViewContact(contact) : undefined}
                  >
                    <div className="row-left">
                      {contact.photoUrl ? (
                        <img src={contact.photoUrl} alt="" className="thumb" />
                      ) : (
                        <div className="thumb placeholder">👤</div>
                      )}
                    </div>
                    <div className="row-mid">
                      <div className="title">{contact.name}</div>
                      <div className="sub">{contact.relationship}</div>
                    </div>
                    <div className="row-right" data-allow-readonly="1">
                      {readOnly ? (
                        <button
                          type="button"
                          data-allow-readonly="1"
                          className="btn btn-main btn-view"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); setViewContact(contact); }}
                        >
                          View
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-main btn-edit"
                          onClick={(e) => { e.stopPropagation(); openForm(contact); }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={editingContact !== null}
        onClose={closeForm}
        title={editingContact?.id ? "Edit Contact" : "Add Contact"}
        maxWidth={560}
      >
        {editingContact ? (
          <div className="contacts-modal">
            <div className="modal-body">
              <div className="grid2">
                <div>
                  <input
                    className="input"
                    placeholder="First name"
                    aria-label="First name"
                    value={ef.firstName}
                    onChange={(e) => setEf({ ...ef, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <input
                    className="input"
                    placeholder="Last name"
                    aria-label="Last name"
                    value={ef.lastName}
                    onChange={(e) => setEf({ ...ef, lastName: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid2">
                <div>
                  <input
                    className="input"
                    type="email"
                    placeholder="Email"
                    aria-label="Email"
                    value={ef.email}
                    onChange={(e) => setEf({ ...ef, email: e.target.value })}
                    autoComplete="email"
                  />
                </div>
                <div>
                  <input
                    className="input"
                    type="tel"
                    placeholder="Phone"
                    aria-label="Phone"
                    value={ef.phone}
                    onChange={(e) => setEf({ ...ef, phone: e.target.value })}
                    autoComplete="tel"
                  />
                </div>
              </div>

              {/* Modern Category Dropdown */}
              <div className="select-menu">
                <button
                  type="button"
                  className="select-menu__button"
                  aria-haspopup="listbox"
                  aria-expanded={catOpen ? "true" : "false"}
                  onClick={() => setCatOpen((v) => !v)}
                >
                  <span className="select-menu__value">{ef.category}</span>
                  <span className="select-menu__chev">▾</span>
                </button>
                {catOpen && (
                  <ul
                    className="select-menu__list"
                    role="listbox"
                    tabIndex={-1}
                    onBlur={() => setCatOpen(false)}
                  >
                    {CATEGORIES.map((c) => (
                      <li
                        key={c}
                        role="option"
                        aria-selected={ef.category === c}
                        className={
                          "select-menu__item" + (ef.category === c ? " is-selected" : "")
                        }
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setEf({ ...ef, category: c });
                          setCatOpen(false);
                        }}
                      >
                        {c}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {editErr && (
                <div className="empty-state" role="alert" style={{ marginTop: 10 }}>
                  {editErr}
                </div>
              )}

              <div className="form-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn-main"
                  disabled={editBusy}
                  onClick={async () => {
                    if (!editingContact?.id) return;
                    const name =
                      [ef.firstName, ef.lastName].filter(Boolean).join(" ").trim() ||
                      ef.email ||
                      ef.phone ||
                      editingContact?.name ||
                      "";
                    if (!name) {
                      setEditErr("Please enter a name, email or phone");
                      return;
                    }
                    setEditBusy(true);
                    setEditErr("");
                    try {
                      await updateDoc(doc(db, "contacts", editingContact.id), {
                        name,
                        firstName: ef.firstName,
                        lastName: ef.lastName,
                        email: ef.email,
                        phone: ef.phone,
                        relationship: ef.category,
                      });
                      closeForm();
                    } catch (e) {
                      setEditErr(e?.message || String(e));
                    } finally {
                      setEditBusy(false);
                    }
                  }}
                >
                  Save
                </button>

                {!readOnly && editingContact?.id ? (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDelete(editingContact)}
                  >
                    Delete
                  </button>
                ) : null}

                <button type="button" className="btn btn-cancel" onClick={closeForm}>
                  Cancel
                </button>
              </div>
            </div>

            <div className="contacts-toolbar bottom">
              <button type="button" className="btn" onClick={handlePrintOwner}>Print</button>
              <button type="button" className="btn" onClick={handleDownloadOwner}>Download</button>
              {editingContact?.photoUrl ? (
                <a className="btn" href={editingContact.photoUrl} target="_blank" rel="noreferrer" download>
                  Download File
                </a>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="contacts-modal">
            <ContactForm
              user={user}
              contact={null}
              onAdded={closeForm}
              onUpdated={closeForm}
              onCancel={closeForm}
              categories={CATEGORIES}
            />
          </div>
        )}
      </Modal>

      {/* View Summary Modal (read-only) */}
      <Modal
        open={!!viewContact}
        onClose={() => setViewContact(null)}
        title="View Contact"
        maxWidth={560}
      >
        {viewContact && (
          <div className="contacts-view" data-allow-readonly="1" ref={viewRef}>
            <div className="view-thumb-wrap">
              {viewContact.photoUrl ? (
                <img src={viewContact.photoUrl} alt="" className="view-thumb" />
              ) : (
                <div className="view-thumb placeholder">👤</div>
              )}
            </div>

            <div className="view-grid">
              <div><b>Name:</b> {viewContact.name || "—"}</div>
              <div><b>Relationship:</b> {viewContact.relationship || "—"}</div>
              <div><b>Email:</b> {viewContact.email || "—"}</div>
              <div><b>Phone:</b> {viewContact.phone || "—"}</div>
              <div className="view-col-2"><b>Address:</b> {viewContact.address || "—"}</div>
              {viewContact.notes ? (
                <div className="view-col-2"><b>Notes:</b> {viewContact.notes}</div>
              ) : null}
            </div>
          </div>
        )}

        {viewContact && (
          <div className="form-actions" style={{ marginTop: 12 }} data-allow-readonly="1">
            <button data-allow-readonly="1" type="button" className="btn btn-main" onMouseDown={(e)=>e.stopPropagation()} onClick={viewPrint}>
              Print
            </button>
            <button data-allow-readonly="1" type="button" className="btn" onMouseDown={(e)=>e.stopPropagation()} onClick={viewDownload}>
              Download
            </button>
            <button data-allow-readonly="1" type="button" className="btn" onMouseDown={(e)=>e.stopPropagation()} onClick={viewForward}>
              Forward
            </button>
            <button data-allow-readonly="1" type="button" className="btn btn-cancel" onMouseDown={(e)=>e.stopPropagation()} onClick={() => setViewContact(null)}>
              Cancel
            </button>
          </div>
        )}
      </Modal>

      <Modal open={showImport} onClose={() => setShowImport(false)} title="Import Contacts" maxWidth={560}>
        <div className="import-body">
          <p className="import-info">
            Upload a .csv file that includes the following column headers (all fields are required):
          </p>
          <ul className="import-list">
            {REQUIRED_IMPORT_FIELDS.map((field) => (
              <li key={field}>{field}</li>
            ))}
          </ul>
          <div
            className={`import-drop ${dragActive ? "is-drag" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const file = e.dataTransfer?.files?.[0];
              if (file) handleImportFile(file);
            }}
            role="presentation"
            aria-busy={importBusy ? "true" : "false"}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImportFile(file);
              }}
            />
            <p className="import-drop-title">Drag & drop your CSV here</p>
            <p className="import-drop-sub">or</p>
            <button
              type="button"
              className="btn btn-main"
              onClick={() => fileInputRef.current?.click()}
              disabled={importBusy}
            >
              {importBusy ? "Uploading…" : "Browse files"}
            </button>
          </div>
          <p className="import-note">
            Need help? Use the template for the correct format and make sure the category is one of:
            <br />
            <b>{CATEGORIES.join(", ")}</b>
          </p>
          {importErr && (
            <div className="import-status error" role="alert">
              {importErr}
            </div>
          )}
          {importSummary && !importErr && (
            <div className="import-status success" role="status">
              {importSummary}
            </div>
          )}
          <div className="form-actions" style={{ marginTop: 10 }}>
            <button type="button" className="btn btn-cancel" onClick={() => setShowImport(false)}>
              Close
            </button>
            <button type="button" className="btn" onClick={downloadTemplateCsv}>
              Download template
            </button>
          </div>
        </div>
      </Modal>

      {/* Global Add Contact modal (unchanged) */}
      <AddContactModal open={showAdd} onClose={() => setShowAdd(false)} user={user} />

      {/* Help video + drawer */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/5dDiNlGyBA8?si=5JRL4WNT5MCeXO1L"
        title="How the Contacts page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="contacts-drawer-title">
            <div className="dp-drawer-head">
              <div className="dp-drawer-title" id="contacts-drawer-title">How the Contacts page works</div>
              <div className="dp-drawer-tools">
                <button data-allow-readonly="1" className="btn btn-main" onClick={() => window.print()} type="button">Print</button>
                <button data-allow-readonly="1" className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button">Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>Use Contacts to store the people who will manage or be informed about your wishes. Categorise them so you can filter quickly when assigning assets or sending messages.</p>
              <h4>Categories</h4>
              <ul>
                {CATEGORIES.map((cat) => (
                  <li key={cat}><b>{cat}</b></li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      )}

      <style>{`
        .header-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .header-actions{display:flex;align-items:center;gap:10px}
        .desktop-only{display:none} .mobile-only{display:block}
        @media(min-width:900px){.desktop-only{display:block}.mobile-only{display:none}}

        .hero-wrap{margin:6px 0 14px}
        .hero-img{width:100%;height:130px;object-fit:cover;border-radius:14px}
        @media (max-width: 700px){
          .hero-img{height:110px}
        }

        /* === ONLY CHANGE REQUESTED ===
           Hide the Import/Download buttons row on small screens */
        .hero-actions{display:flex;gap:12px;flex-wrap:wrap;margin:12px 0 18px}
        .hero-btn{display:inline-flex;align-items:center;gap:8px;font-weight:700}
        .hero-btn svg{font-size:1.1rem}
        .import-btn:disabled{opacity:.5;cursor:not-allowed}
        .template-btn{background:#fff;border:1px solid #d6d6ea;color:#2a0516;font-weight:700}
        .template-btn:hover{background:#f6f6fb}
        @media (max-width: 700px){
          .hero-actions{display:none}
        }

        .dp-help-row{display:flex;gap:10px;flex-wrap:wrap}
        .dp-help{border-radius:8px;padding:10px 14px;display:inline-flex;align-items:center;gap:10px;font-weight:700;color:#fff;border:none}
        .dp-help.read{background:#650202;color:#fff}
        .dp-help.see{background:#003353;color:#fff}
        .dp-help-ico{font-size:1.05rem}

        .cat-filter{position:sticky;top:8px;z-index:2;background:transparent;margin:6px 0 14px}
        .cat-pills{display:flex;gap:8px;flex-wrap:wrap}
        .pill{padding:8px 12px;border-radius:999px;border:1px solid #d6d6ea;background:#fff;color:#2a0516;font-weight:700;cursor:pointer}
        .pill.active{background:#2a0516;color:#fff;border-color:#2a0516}
        .cat-select{margin-top:8px;width:100%;}
        .cat-select select{margin-bottom:0;}
        @media(min-width:700px){ .cat-select{display:none} }
        @media(max-width:700px){ .cat-pills{display:none} }

        .contacts-groups{display:grid;gap:18px}
        .section-title.small{font-weight:800;color:#2a0516;opacity:.9;font-size:14px;margin-left:6px}
        .contacts-list{display:grid;gap:10px}

        .contact-row{display:grid;grid-template-columns:auto 1fr auto;gap:12px;align-items:center;background:#fff;border:1px solid #ececf5;border-radius:12px;padding:12px;min-width:0}
        .row-left{display:flex}
        .thumb{width:68px;height:68px;border-radius:10px;object-fit:cover;background:#f4f5f9;display:block}
        .thumb.placeholder{display:flex;align-items:center;justify-content:center;font-size:24px}
        .row-mid{min-width:0}
        .thumb.placeholder{display:flex;align-items:center;justify-content:center;font-size:24px}
        .title{font-weight:700;color:#2a0516}
        .sub{color:#657899}
        .row-right{display:flex;gap:8px;align-items:center;justify-content:flex-end}
        .row-right .btn{white-space:nowrap}
        @media(max-width:600px){ .contact-row{gap:10px;padding:10px} .thumb{width:56px;height:56px} }

        .contacts-modal > * h3:first-child{display:none}
        .contacts-toolbar.bottom{display:flex;gap:8px;flex-wrap:wrap;margin-top:12px}
        .contacts-toolbar.bottom .btn{flex:1}

        .contacts-view{display:grid;gap:10px}
        .view-thumb-wrap{display:flex;justify-content:center}
        .view-thumb{width:120px;height:120px;border-radius:12px;object-fit:cover;background:#f4f5f9;border:1px solid #ececf5}
        .view-thumb.placeholder{width:120px;height:120px;border-radius:12px;background:#f4f5f9;display:flex;align-items:center;justify-content:center;font-size:42px;border:1px solid #ececf5}

        .view-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
        .view-col-2{grid-column:1 / -1}

        .import-body{display:flex;flex-direction:column;gap:12px}
        .import-info{margin:0;color:#4c4c63}
        .import-list{margin:0;padding-left:18px;color:#2a0516;font-weight:600}
        .import-drop{border:2px dashed #c4c6da;border-radius:16px;padding:24px;text-align:center;background:#f9f9fc;transition:background .2s,border-color .2s}
        .import-drop.is-drag{background:#fff;border-color:#2a0516}
        .import-drop-title{font-size:1rem;font-weight:700;margin:0}
        .import-drop-sub{margin:6px 0}
        .import-note{margin:0;color:#4c4c63;font-size:.95rem}
        .import-status{padding:10px 12px;border-radius:10px;font-weight:600}
        .import-status.error{background:#ffe8e8;color:#7d0000}
        .import-status.success{background:#e9f7ef;color:#0f5132}

        /* ===== Modern category select (Edit modal only) ===== */
        .select-menu{ position:relative; margin-top:10px; }
        .select-menu__button{
          width:100%;
          display:flex; align-items:center; justify-content:space-between;
          padding:10px 12px;
          border:1px solid #d6d6ea; border-radius:10px;
          background:#fff; color:#2a0516; font-weight:600; cursor:pointer;
        }
        .select-menu__button:focus{ outline:2px solid #2a0516; outline-offset:2px; }
        .select-menu__value{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .select-menu__chev{ opacity:.7 }
        .select-menu__list{
          position:absolute; left:0; right:0; top:calc(100% + 6px);
          background:#fff; border:1px solid #ececf5; border-radius:12px;
          box-shadow:0 10px 24px rgba(0,0,0,.08);
          max-height:260px; overflow:auto; padding:6px;
          z-index:20;
        }
        .select-menu__item{ padding:10px 12px; border-radius:8px; cursor:pointer; font-weight:600; color:#2a0516; }
        .select-menu__item:hover{ background:#f6f7fb; }
        .select-menu__item.is-selected{ background:#2a0516; color:#fff; }
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="messages" />
    </div>
  );
}
